class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.initQaOpen();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    this.classList.remove('is-just-added');
    const cartBubble = document.getElementById('cart-icon-bubble');
    cartBubble?.classList.remove('is-added-feedback');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  initQaOpen() {
    const url = new URL(window.location.href);
    const qaRequested = url.searchParams.get('rom_cart_qa') === 'open' || url.hash === '#rom-cart-qa';

    if (!qaRequested || this.dataset.qaReady === 'true') return;

    this.dataset.qaReady = 'true';
    url.searchParams.delete('rom_cart_qa');
    if (url.hash === '#rom-cart-qa') url.hash = '';
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);

    window.addEventListener(
      'load',
      () => {
        setTimeout(() => {
          this.open(document.querySelector('#cart-icon-bubble'));
        }, 900);
      },
      { once: true }
    );
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.showAddFeedback(parsedState);
      this.open();
    });
  }

  showAddFeedback(parsedState) {
    const confirmation = this.querySelector('.cart-drawer__confirmation');
    const confirmationText = confirmation?.querySelector('.cart-drawer__confirmation-text');
    const upsells = this.querySelector('.cart-drawer__upsells');
    const cartBubble = document.getElementById('cart-icon-bubble');

    if (this.addFeedbackTimer) clearTimeout(this.addFeedbackTimer);
    if (this.upsellHighlightTimer) clearTimeout(this.upsellHighlightTimer);
    if (this.cartBubbleTimer) clearTimeout(this.cartBubbleTimer);

    this.classList.add('is-just-added');

    if (confirmation && confirmationText) {
      const addedLabel = parsedState?.product_title || parsedState?.title || parsedState?.items?.[0]?.product_title || 'Item';
      confirmationText.textContent = upsells
        ? `${addedLabel} is in the cart. Suggested add-ons appear below checkout.`
        : `${addedLabel} is in the cart. Review your order below.`;

      confirmation.hidden = false;
      confirmation.classList.remove('is-visible');

      requestAnimationFrame(() => {
        confirmation.classList.add('is-visible');
      });

      this.addFeedbackTimer = setTimeout(() => {
        confirmation.classList.remove('is-visible');
        setTimeout(() => {
          confirmation.hidden = true;
        }, 240);
        this.classList.remove('is-just-added');
      }, 4200);
    }

    if (upsells) {
      upsells.classList.remove('is-highlighted');
      requestAnimationFrame(() => {
        upsells.classList.add('is-highlighted');
      });
      this.upsellHighlightTimer = setTimeout(() => {
        upsells.classList.remove('is-highlighted');
      }, 3200);
    }

    if (cartBubble) {
      cartBubble.classList.remove('is-added-feedback');
      requestAnimationFrame(() => {
        cartBubble.classList.add('is-added-feedback');
      });
      this.cartBubbleTimer = setTimeout(() => {
        cartBubble.classList.remove('is-added-feedback');
      }, 2200);
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
